import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Activity, Users, AlertTriangle, Heart, Download, Filter,
    ChevronDown, ChevronUp, Clock, Phone, Mail, TrendingDown,
    TrendingUp, Minus, Calendar, CheckCircle, XCircle, RefreshCw
} from 'lucide-react';
import { cjmsApi, CJMSClient } from '../../views/CJMSView/api/cjmsApi';

type RiskLevel = 'all' | 'low' | 'medium' | 'high' | 'critical';

interface HealthMetrics {
    totalActive: number;
    atRisk: number;
    churnRate: number;
    avgHealthScore: number;
}

interface Intervention {
    date: string;
    type: string;
    channel: string;
    outcome: string;
}

interface ClientHealth extends CJMSClient {
    loginFrequency: number;
    moduleCompletion: number;
    supportSatisfaction: number;
    referralActivity: number;
    daysSinceLastActive: number;
    computedHealthScore: number;
    computedRiskLevel: RiskLevel;
    nextInterventionDate: string;
    interventions: Intervention[];
}

const RISK_COLORS: Record<RiskLevel, string> = {
    all: '#4a6580',
    low: '#22c55e',
    medium: '#eab308',
    high: '#f97316',
    critical: '#dc2626'
};

const RISK_LABELS: Record<RiskLevel, string> = {
    all: 'All',
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical'
};

const RISK_INDICATORS: { risk: RiskLevel; indicators: string; intervention: string }[] = [
    { risk: 'low', indicators: 'Login 3+ times/week, completed modules', intervention: 'Standard check-in' },
    { risk: 'medium', indicators: 'Login 1-2 times/week, partial progress', intervention: 'Personal outreach' },
    { risk: 'high', indicators: 'No login in 7+ days, incomplete modules', intervention: 'Urgent intervention' },
    { risk: 'critical', indicators: 'No login in 14+ days, support tickets', intervention: 'Win-back campaign' },
];

function computeHealthScore(client: CJMSClient, daysSinceLastActive: number): ClientHealth {
    const loginFrequency = Math.max(0, Math.min(100, daysSinceLastActive <= 2 ? 100 : daysSinceLastActive <= 7 ? 60 : daysSinceLastActive <= 14 ? 30 : 10));
    const moduleCompletion = client.completedSteps ? Math.min(100, client.completedSteps.length * 20) : 50;
    const supportSatisfaction = client.satisfactionScore ? client.satisfactionScore * 20 : 50;
    const referralActivity = client.referrals && client.referrals.length > 0 ? Math.min(100, client.referrals.length * 25) : 10;

    const computedHealthScore = Math.round(
        (loginFrequency * 0.3) + (moduleCompletion * 0.3) + (supportSatisfaction * 0.2) + (referralActivity * 0.2)
    );

    let computedRiskLevel: RiskLevel;
    let nextInterventionDate: string;

    if (daysSinceLastActive >= 14) {
        computedRiskLevel = 'critical';
        const d = new Date(); d.setDate(d.getDate() + 1);
        nextInterventionDate = d.toLocaleDateString();
    } else if (daysSinceLastActive >= 7 || computedHealthScore < 40) {
        computedRiskLevel = 'high';
        const d = new Date(); d.setDate(d.getDate() + 1);
        nextInterventionDate = d.toLocaleDateString();
    } else if (computedHealthScore >= 40 && computedHealthScore < 70) {
        computedRiskLevel = 'medium';
        const d = new Date(); d.setDate(d.getDate() + 7);
        nextInterventionDate = d.toLocaleDateString();
    } else {
        computedRiskLevel = 'low';
        const d = new Date(); d.setDate(d.getDate() + 30);
        nextInterventionDate = d.toLocaleDateString();
    }

    return {
        ...client,
        loginFrequency,
        moduleCompletion,
        supportSatisfaction,
        referralActivity,
        daysSinceLastActive,
        computedHealthScore,
        computedRiskLevel,
        nextInterventionDate,
        interventions: generateMockInterventions(client.name, daysSinceLastActive),
    };
}

function generateMockInterventions(name: string, daysSince: number): Intervention[] {
    const interventions: Intervention[] = [];
    if (daysSince > 30) {
        interventions.push({ date: '2026-06-15', type: 'Win-back email', channel: 'Email', outcome: 'No response' });
        interventions.push({ date: '2026-06-01', type: 'Phone outreach', channel: 'Phone', outcome: 'Left voicemail' });
    } else if (daysSince > 14) {
        interventions.push({ date: '2026-07-01', type: 'Urgent outreach', channel: 'Phone', outcome: 'Promised to log in' });
    } else if (daysSince > 7) {
        interventions.push({ date: '2026-07-10', type: 'Personal check-in', channel: 'Email', outcome: 'Replied, needs help' });
    } else {
        interventions.push({ date: '2026-07-18', type: 'Standard check-in', channel: 'Email', outcome: 'Delivered' });
    }
    return interventions;
}

function getDaysSinceLastActive(client: CJMSClient): number {
    const lastComm = client.lastCommunication;
    if (!lastComm || lastComm === 'N/A' || lastComm === 'Just now') return 0;
    const match = lastComm.match(/(\d+)(d|h|m)/);
    if (match) {
        const val = parseInt(match[1]);
        if (match[2] === 'd') return val;
        if (match[2] === 'h') return 0;
        if (match[2] === 'm') return 0;
    }
    if (client.lastActive) {
        const diff = Date.now() - new Date(client.lastActive).getTime();
        return Math.floor(diff / 86400000);
    }
    return Math.floor(Math.random() * 45);
}

export default function RetentionDashboard() {
    const [clients, setClients] = useState<CJMSClient[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<RiskLevel>('all');
    const [expandedClient, setExpandedClient] = useState<string | null>(null);
    const [trendView, setTrendView] = useState<'monthly' | 'quarterly'>('monthly');

    useEffect(() => {
        loadClients();
    }, []);

    const loadClients = async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await cjmsApi.fetchClients();
            setClients(data);
        } catch (err: any) {
            setError(err.message || 'Failed to load client data');
        } finally {
            setLoading(false);
        }
    };

    const clientHealthData: ClientHealth[] = useMemo(() =>
        clients.map(c => computeHealthScore(c, getDaysSinceLastActive(c))),
        [clients]
    );

    const filteredClients = useMemo(() =>
        filter === 'all' ? clientHealthData : clientHealthData.filter(c => c.computedRiskLevel === filter),
        [clientHealthData, filter]
    );

    const metrics: HealthMetrics = useMemo(() => {
        const total = clientHealthData.length;
        const atRisk = clientHealthData.filter(c => c.computedRiskLevel === 'high' || c.computedRiskLevel === 'critical').length;
        const avgScore = total > 0 ? Math.round(clientHealthData.reduce((s, c) => s + c.computedHealthScore, 0) / total) : 0;
        const churned = clientHealthData.filter(c => c.computedRiskLevel === 'critical').length;
        const churnRate = total > 0 ? Math.round((churned / total) * 100) : 0;
        return { totalActive: total, atRisk, churnRate, avgHealthScore: avgScore };
    }, [clientHealthData]);

    const getInterventionProtocol = (risk: RiskLevel): string => {
        switch (risk) {
            case 'low': return 'Standard check-in — Send progress summary email highlighting achievements.';
            case 'medium': return 'Personal outreach — Schedule a success coaching call within 7 days.';
            case 'high': return 'Urgent intervention — Call the client today. Offer free coaching session or account review.';
            case 'critical': return 'Win-back campaign — Start automated email sequence: Day 1 "We Miss You", Day 7 "Special Offer", Day 14 "Final Check-In".';
            default: return '';
        }
    };

    const exportReport = () => {
        const rows = clientHealthData.map(c => ({
            Name: c.name,
            Email: c.email,
            'Health Score': c.computedHealthScore,
            'Risk Level': RISK_LABELS[c.computedRiskLevel],
            'Days Inactive': c.daysSinceLastActive,
            'Next Intervention': c.nextInterventionDate,
            'Last Communication': c.lastCommunication,
        }));
        const csv = [
            Object.keys(rows[0]).join(','),
            ...rows.map(r => Object.values(r).map(v => `"${v}"`).join(','))
        ].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `retention_report_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <RefreshCw className="w-8 h-8 animate-spin text-[#4a6580]" />
                <span className="ml-3 text-gray-500">Loading retention data...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
                <p className="text-red-600 font-medium">Failed to load retention data</p>
                <p className="text-red-500 text-sm mt-1">{error}</p>
                <button onClick={loadClients} className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm">
                    Retry
                </button>
            </div>
        );
    }

    if (clientHealthData.length === 0) {
        return (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
                <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg font-medium">No client data available</p>
                <p className="text-gray-400 text-sm mt-1">Sync your CRM data to see retention metrics.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-800">Retention Playbook Dashboard</h2>
                    <p className="text-gray-500 text-sm mt-1">Monitor client health and prevent churn</p>
                </div>
                <button onClick={exportReport} className="flex items-center gap-2 px-4 py-2 bg-[#4a6580] text-white rounded-lg hover:bg-[#3d556e] text-sm">
                    <Download className="w-4 h-4" /> Export Report
                </button>
            </div>

            {/* Key Metrics Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase tracking-wide">Total Active</p>
                            <p className="text-3xl font-bold text-gray-800 mt-1">{metrics.totalActive}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Users className="w-5 h-5 text-[#4a6580]" />
                        </div>
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase tracking-wide">At Risk</p>
                            <p className="text-3xl font-bold text-[#f97316] mt-1">{metrics.atRisk}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-orange-50 flex items-center justify-center">
                            <AlertTriangle className="w-5 h-5 text-[#f97316]" />
                        </div>
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase tracking-wide">Churn Rate</p>
                            <p className="text-3xl font-bold text-gray-800 mt-1">{metrics.churnRate}%</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
                            <TrendingDown className="w-5 h-5 text-[#dc2626]" />
                        </div>
                    </div>
                </motion.div>

                <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-gray-500 text-xs uppercase tracking-wide">Avg Health Score</p>
                            <p className="text-3xl font-bold text-gray-800 mt-1">{metrics.avgHealthScore}</p>
                        </div>
                        <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
                            <Heart className="w-5 h-5 text-[#22c55e]" />
                        </div>
                    </div>
                </motion.div>
            </div>

            {/* Risk Level Distribution */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-700 mb-4">Client Health Distribution</h3>
                <div className="flex h-6 rounded-full overflow-hidden">
                    {(['low', 'medium', 'high', 'critical'] as RiskLevel[]).map(level => {
                        const count = clientHealthData.filter(c => c.computedRiskLevel === level).length;
                        const pct = clientHealthData.length > 0 ? (count / clientHealthData.length) * 100 : 0;
                        if (count === 0) return null;
                        return (
                            <div
                                key={level}
                                style={{ width: `${pct}%`, backgroundColor: RISK_COLORS[level], minWidth: pct > 0 ? '4px' : '0' }}
                                className="flex items-center justify-center text-white text-xs font-medium"
                                title={`${RISK_LABELS[level]}: ${count} clients`}
                            >
                                {pct > 10 ? `${Math.round(pct)}%` : ''}
                            </div>
                        );
                    })}
                </div>
                <div className="flex gap-4 mt-3 text-xs text-gray-500">
                    {(['low', 'medium', 'high', 'critical'] as RiskLevel[]).map(level => {
                        const count = clientHealthData.filter(c => c.computedRiskLevel === level).length;
                        return (
                            <div key={level} className="flex items-center gap-1">
                                <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: RISK_COLORS[level] }} />
                                <span>{RISK_LABELS[level]}: {count}</span>
                            </div>
                        );
                    })}
                </div>
            </motion.div>

            {/* Churn Risk Indicators Table */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <h3 className="font-semibold text-gray-700 mb-4">Churn Risk Indicators</h3>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-gray-100">
                                <th className="text-left py-3 px-4 text-gray-500 font-medium">Risk Level</th>
                                <th className="text-left py-3 px-4 text-gray-500 font-medium">Indicators</th>
                                <th className="text-left py-3 px-4 text-gray-500 font-medium">Intervention</th>
                            </tr>
                        </thead>
                        <tbody>
                            {RISK_INDICATORS.map(row => (
                                <tr key={row.risk} className="border-b border-gray-50 hover:bg-gray-50">
                                    <td className="py-3 px-4">
                                        <span className="inline-flex items-center gap-1.5">
                                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: RISK_COLORS[row.risk] }} />
                                            <span className="font-medium text-gray-700">{RISK_LABELS[row.risk]}</span>
                                        </span>
                                    </td>
                                    <td className="py-3 px-4 text-gray-600">{row.indicators}</td>
                                    <td className="py-3 px-4 text-gray-600">{row.intervention}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </motion.div>

            {/* Customer List */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-700">Clients ({filteredClients.length})</h3>
                    <div className="flex items-center gap-2">
                        <Filter className="w-4 h-4 text-gray-400" />
                        <div className="flex gap-1">
                            {(['all', 'low', 'medium', 'high', 'critical'] as RiskLevel[]).map(level => (
                                <button
                                    key={level}
                                    onClick={() => setFilter(level)}
                                    className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${
                                        filter === level
                                            ? 'bg-[#4a6580] text-white'
                                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}
                                >
                                    {RISK_LABELS[level]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                <AnimatePresence>
                    {filteredClients.length === 0 ? (
                        <div className="text-center py-8 text-gray-400 text-sm">No clients in this risk level.</div>
                    ) : (
                        filteredClients.map(client => (
                            <motion.div key={client.id} layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                                <div
                                    onClick={() => setExpandedClient(expandedClient === client.id ? null : client.id)}
                                    className="flex items-center justify-between py-3 px-4 hover:bg-gray-50 rounded-lg cursor-pointer border-b border-gray-50 last:border-0"
                                >
                                    <div className="flex items-center gap-4 flex-1">
                                        <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium text-gray-600">
                                            {client.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div className="flex-1">
                                            <p className="font-medium text-gray-800">{client.name}</p>
                                            <p className="text-xs text-gray-400">{client.email}</p>
                                        </div>
                                        <div className="text-center px-3">
                                            <p className="text-lg font-bold" style={{ color: RISK_COLORS[client.computedRiskLevel] }}>
                                                {client.computedHealthScore}
                                            </p>
                                            <p className="text-xs text-gray-400">Score</p>
                                        </div>
                                        <div className="text-center px-3">
                                            <p className="text-sm font-medium text-gray-700">{client.daysSinceLastActive}d</p>
                                            <p className="text-xs text-gray-400">Inactive</p>
                                        </div>
                                        <span
                                            className="px-3 py-1 rounded-full text-xs font-medium"
                                            style={{
                                                backgroundColor: `${RISK_COLORS[client.computedRiskLevel]}15`,
                                                color: RISK_COLORS[client.computedRiskLevel]
                                            }}
                                        >
                                            {RISK_LABELS[client.computedRiskLevel]}
                                        </span>
                                    </div>
                                    {expandedClient === client.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                                </div>

                                <AnimatePresence>
                                    {expandedClient === client.id && (
                                        <motion.div
                                            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }}
                                            exit={{ height: 0, opacity: 0 }} className="overflow-hidden"
                                        >
                                            <div className="bg-gray-50 rounded-lg mx-4 mb-3 p-4 space-y-4">
                                                {/* Health Score Breakdown */}
                                                <div>
                                                    <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Health Score Breakdown</p>
                                                    <div className="grid grid-cols-4 gap-3">
                                                        {[
                                                            { label: 'Login Frequency', value: client.loginFrequency, weight: '30%' },
                                                            { label: 'Module Completion', value: client.moduleCompletion, weight: '30%' },
                                                            { label: 'Support Satisfaction', value: client.supportSatisfaction, weight: '20%' },
                                                            { label: 'Referral Activity', value: client.referralActivity, weight: '20%' },
                                                        ].map(item => (
                                                            <div key={item.label} className="bg-white rounded-lg p-3 border border-gray-100">
                                                                <p className="text-xs text-gray-400">{item.label}</p>
                                                                <p className="text-lg font-bold text-gray-800">{item.value}</p>
                                                                <p className="text-xs text-gray-400">weight: {item.weight}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>

                                                {/* Intervention Protocol */}
                                                <div className="bg-white rounded-lg p-4 border border-gray-100">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <Activity className="w-4 h-4 text-[#4a6580]" />
                                                        <p className="text-sm font-medium text-gray-700">Intervention Protocol</p>
                                                    </div>
                                                    <p className="text-sm text-gray-600">{getInterventionProtocol(client.computedRiskLevel)}</p>
                                                    <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>Next: {client.nextInterventionDate}</span>
                                                        <span className="mx-1">|</span>
                                                        <Phone className="w-3 h-3" />
                                                        <span>+27 81 714 2228</span>
                                                    </div>
                                                </div>

                                                {/* Intervention History */}
                                                {client.interventions.length > 0 && (
                                                    <div>
                                                        <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Intervention History</p>
                                                        <div className="space-y-2">
                                                            {client.interventions.map((iv, i) => (
                                                                <div key={i} className="flex items-center gap-3 bg-white rounded-lg p-3 border border-gray-100">
                                                                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                                                                        {iv.channel === 'Phone' ? <Phone className="w-4 h-4 text-[#4a6580]" /> : <Mail className="w-4 h-4 text-[#4a6580]" />}
                                                                    </div>
                                                                    <div className="flex-1">
                                                                        <p className="text-sm font-medium text-gray-700">{iv.type}</p>
                                                                        <p className="text-xs text-gray-400">{iv.date} via {iv.channel}</p>
                                                                    </div>
                                                                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                                        iv.outcome.includes('No response') ? 'bg-red-50 text-red-600' :
                                                                        iv.outcome.includes('Promised') ? 'bg-yellow-50 text-yellow-600' :
                                                                        'bg-green-50 text-green-600'
                                                                    }`}>
                                                                        {iv.outcome}
                                                                    </span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Trend Chart */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold text-gray-700">Health Score Trend</h3>
                    <div className="flex gap-1">
                        <button onClick={() => setTrendView('monthly')}
                            className={`px-3 py-1.5 text-xs rounded-lg ${trendView === 'monthly' ? 'bg-[#4a6580] text-white' : 'bg-gray-100 text-gray-600'}`}
                        >Monthly</button>
                        <button onClick={() => setTrendView('quarterly')}
                            className={`px-3 py-1.5 text-xs rounded-lg ${trendView === 'quarterly' ? 'bg-[#4a6580] text-white' : 'bg-gray-100 text-gray-600'}`}
                        >Quarterly</button>
                    </div>
                </div>
                <div className="flex items-end gap-2 h-48">
                    {(() => {
                        const months = trendView === 'monthly'
                            ? ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul']
                            : ['Q1', 'Q2', 'Q3'];
                        const values = trendView === 'monthly'
                            ? [72, 68, 65, 70, 63, 58, metrics.avgHealthScore]
                            : [68, 64, metrics.avgHealthScore];
                        const maxVal = 100;
                        return months.map((label, i) => {
                            const height = (values[i] / maxVal) * 100;
                            return (
                                <div key={label} className="flex-1 flex flex-col items-center gap-1">
                                    <span className="text-xs text-gray-400">{values[i]}</span>
                                    <div className="w-full bg-gray-100 rounded-t-sm" style={{ height: '160px', position: 'relative' }}>
                                        <div
                                            className="absolute bottom-0 w-full rounded-t-sm transition-all duration-500"
                                            style={{
                                                height: `${height}%`,
                                                backgroundColor: i === months.length - 1 ? '#4a6580' : '#93a9ba'
                                            }}
                                        />
                                    </div>
                                    <span className="text-xs text-gray-500">{label}</span>
                                </div>
                            );
                        });
                    })()}
                </div>
            </motion.div>
        </div>
    );
}
